import {
    Download,
    Upload,
    Visibility,
    CheckCircle,
    Error,
    Schedule,
    CloudUpload,
    Warning,
    InfoOutlined,
    OpenInNew,
    DeleteOutlined,
} from '@mui/icons-material';
import {
    Card,
    CardContent,
    Typography,
    Chip,
    Button,
    Box,
    CircularProgress,
    Stack,
    IconButton,
    ClickAwayListener,
    Tooltip,
    type TooltipProps,
    type SxProps,
    type Theme,
    tooltipClasses,
    styled,
} from '@mui/material';
import { type ReactNode, useState, useRef, useEffect, useCallback, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { DocumentStatus } from 'constant';
import { linkifyHtml } from 'utils/v2/document';
import sanitizeHtml from 'utils/v2/sanitizeHtml';
import type { ApplicationDocumentInterface } from 'types';

/* ── Styled tooltip ── */
const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: '#f5f5f9',
        color: 'rgba(0, 0, 0, 0.87)',
        maxWidth: 360,
        fontSize: theme.typography.pxToRem(12),
        border: '1px solid #dadde9',
        boxShadow: theme.shadows[4],
        whiteSpace: 'normal',
        '& ul, & ol': { margin: 0, paddingLeft: theme.spacing(2) },
        '& li': { marginBottom: theme.spacing(0.5) },
        '& a': { color: '#6d41d8', textDecoration: 'underline', '&:hover': { color: '#8353e2' } },
    },
}));

/* ── ActionButton — single reusable button component ── */
type ActionButtonVariant = 'default' | 'warning' | 'dashed' | 'primary' | 'error';

interface ActionButtonProps {
    label: string;
    icon: ReactNode;
    onClick: () => void;
    variant?: ActionButtonVariant;
    disabled?: boolean;
    fullWidth?: boolean;
}

const variantSx: Record<ActionButtonVariant, SxProps<Theme>> = {
    default: {
        borderColor: 'divider',
        color: 'text.secondary',
        '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.50', color: 'primary.main' },
    },
    warning: {
        borderColor: 'divider',
        color: 'text.secondary',
        '&:hover': { borderColor: 'warning.main', bgcolor: 'warning.50', color: 'warning.main' },
    },
    dashed: {
        borderStyle: 'dashed',
        borderColor: 'divider',
        color: 'text.secondary',
        '&:hover': {
            borderStyle: 'dashed',
            borderColor: 'primary.main',
            bgcolor: 'action.hover',
            color: 'primary.main',
        },
    },
    primary: {
        fontWeight: 600,
    },
    error: {
        borderColor: 'divider',
        color: 'text.secondary',
        '&:hover': { borderColor: 'error.main', bgcolor: 'error.50', color: 'error.main' },
    },
};

const baseSx: SxProps<Theme> = {
    fontSize: '0.75rem',
    textTransform: 'none',
    py: 0.75,
    minWidth: 'auto',
    justifyContent: 'center',
};

const ActionButton = ({
    label,
    icon,
    onClick,
    variant = 'default',
    disabled = false,
    fullWidth = false,
}: ActionButtonProps) => (
    <Button
        size="small"
        variant={variant === 'primary' ? 'contained' : 'outlined'}
        fullWidth={fullWidth}
        disabled={disabled}
        onClick={onClick}
        startIcon={icon}
        sx={{ ...baseSx, ...variantSx[variant] }}
    >
        {label}
    </Button>
);

/* ── Helpers ── */
interface DocumentCardProps {
    document: ApplicationDocumentInterface;
    isDownloading?: boolean;
    canUpload?: boolean;
    viewMode?: 'grid' | 'row';
    handleAction: (action: string, document: ApplicationDocumentInterface) => void;
    getTemplateUrl?: (document: ApplicationDocumentInterface) => string | null;
}

const getStatusColor = (status: string): 'default' | 'error' | 'warning' | 'success' | 'info' => {
    switch (status) {
        case DocumentStatus.APPROVED:
        case DocumentStatus.PREAPPROVED:
            return 'success';
        case DocumentStatus.MISSING:
            return 'error';
        case DocumentStatus.FIX_REQUIRED:
        case DocumentStatus.SIGNATURE_REQUIRED:
            return 'warning';
        case DocumentStatus.WAITING_FOR_APPROVAL:
            return 'info';
        default:
            return 'default';
    }
};

const getStatusIcon = (status: DocumentStatus) => {
    switch (status) {
        case DocumentStatus.APPROVED:
        case DocumentStatus.PREAPPROVED:
            return <CheckCircle fontSize="small" />;
        case DocumentStatus.FIX_REQUIRED:
            return <Warning fontSize="small" />;
        case DocumentStatus.WAITING_FOR_APPROVAL:
            return <Schedule fontSize="small" />;
        default:
            return <Error fontSize="small" />;
    }
};

const formatStatusLabel = (status: string) =>
    status
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b(\w)/g, (m) => m.toUpperCase());

/* ── Shared description HTML styles ── */
const descHtmlSx = {
    '& ul, & ol': { margin: 0, paddingLeft: 2 },
    '& li': { marginBottom: 0.25 },
    '& p': { margin: 0 },
    '& a': {
        color: '#6d41d8',
        textDecoration: 'underline',
        '&:hover': { color: '#8353e2' },
    },
} as const;

/* ── DocumentCard ── */
const DocumentCard = ({
    document,
    isDownloading = false,
    canUpload = true,
    viewMode = 'grid',
    handleAction,
    getTemplateUrl,
}: DocumentCardProps) => {
    const [descExpanded, setDescExpanded] = useState(false);
    const [openInfo, setOpenInfo] = useState(false);
    const [isClamped, setIsClamped] = useState(false);
    const descRef = useRef<HTMLDivElement>(null);
    const { t } = useTranslation();

    const hasFile = Boolean(document.resource_name);
    const isFixRequired =
        document.status === 'FIX_REQUIRED' || document.status === 'SIGNATURE_REQUIRED';
    const isRow = viewMode === 'row';

    const documentName = document.document_name || document.name_en || document.name_fr;
    const documentDescription =
        document.explanation || document.description_en || document.description_fr;

    // "Template from URL" docs have no uploaded file but link out via their description.
    const templateUrl = document.template_uri ? null : (getTemplateUrl?.(document) ?? null);
    const hasTemplate = Boolean(document.template_uri) || Boolean(templateUrl);
    const hasSample = Boolean(document.sample_uri);

    // Same delete rules as CLIENTFLOW Documents.tsx actions column
    const canDelete =
        canUpload &&
        hasFile &&
        document.status !== DocumentStatus.MISSING &&
        document.status !== DocumentStatus.APPROVED &&
        document.status !== DocumentStatus.PREAPPROVED;

    const checkClamped = useCallback(() => {
        const el = descRef.current;
        if (el) {
            setIsClamped(el.scrollHeight > el.clientHeight);
        }
    }, []);

    useEffect(() => {
        if (!descExpanded) checkClamped();
    }, [descExpanded, documentDescription, checkClamped]);

    const handleInfoClick = (e: MouseEvent) => {
        e.stopPropagation();
        setOpenInfo((s) => !s);
    };
    const handleInfoClose = () => setOpenInfo(false);

    const fire = (action: string) => () => handleAction(action, document);

    const renderSecondaryActions = () => (
        <>
            {hasSample && (
                <ActionButton
                    label={t('documentsSample')}
                    icon={<Visibility fontSize="small" />}
                    onClick={fire('VIEW_SAMPLE')}
                    variant="dashed"
                />
            )}
            {hasTemplate && (
                <ActionButton
                    label={t('template')}
                    icon={
                        templateUrl ? (
                            <OpenInNew fontSize="small" />
                        ) : (
                            <Visibility fontSize="small" />
                        )
                    }
                    onClick={fire('VIEW_TEMPLATE')}
                    variant="dashed"
                />
            )}
        </>
    );

    const renderActions = () => {
        if (hasFile) {
            return (
                <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{ flexWrap: 'wrap', alignItems: 'center' }}
                    useFlexGap
                >
                    <ActionButton
                        label={t('documentsView')}
                        icon={<Visibility fontSize="small" />}
                        onClick={fire('DOCUMENT_PREVIEW')}
                        disabled={isDownloading}
                    />
                    <ActionButton
                        label={isDownloading ? t('loading') : t('download')}
                        icon={
                            isDownloading ? (
                                <CircularProgress size={14} />
                            ) : (
                                <Download fontSize="small" />
                            )
                        }
                        onClick={fire('DOWNLOAD')}
                        disabled={isDownloading}
                    />
                    {canUpload && (
                        <ActionButton
                            label={t('documentsReplace')}
                            icon={<Upload fontSize="small" />}
                            onClick={fire('REPLACE')}
                            variant="warning"
                        />
                    )}
                    {canDelete && (
                        <ActionButton
                            label={t('delete')}
                            icon={<DeleteOutlined fontSize="small" />}
                            onClick={fire('DELETE')}
                            variant="error"
                        />
                    )}
                    {renderSecondaryActions()}
                </Stack>
            );
        }

        /* No file uploaded yet — upload + sample/template (CLIENTFLOW) */
        return (
            <Stack
                direction="row"
                spacing={0.5}
                sx={{ flexWrap: 'wrap', alignItems: 'center' }}
                useFlexGap
            >
                {canUpload && (
                    <ActionButton
                        label={t('documentsUpload')}
                        icon={<CloudUpload fontSize="small" />}
                        onClick={fire('DOCUMENT_UPLOAD')}
                        variant="primary"
                    />
                )}
                {renderSecondaryActions()}
            </Stack>
        );
    };

    return (
        <Card
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: isRow ? 'row' : 'column',
                border: '1px solid',
                borderColor: isFixRequired ? 'warning.main' : 'divider',
                backgroundColor: isFixRequired ? 'warning.50' : 'inherit',
                transition: 'all 0.12s ease',
                '&:hover': {
                    borderColor: isFixRequired ? 'warning.dark' : 'primary.light',
                    boxShadow: 2,
                },
            }}
        >
            <CardContent
                sx={{
                    flexGrow: 1,
                    p: { xs: 1.25, sm: 1.5 },
                    '&:last-child': { pb: isRow ? undefined : 1 },
                }}
            >
                {isRow ? (
                    /* ═══ ROW / LIST LAYOUT ═══ */
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {/* Status chip */}
                        <Chip
                            size="small"
                            color={getStatusColor(document.status)}
                            icon={getStatusIcon(document.status)}
                            label={formatStatusLabel(document.status)}
                            sx={{ flexShrink: 0 }}
                        />

                        {/* Name + full description */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600, fontSize: '0.875rem' }}
                            >
                                {documentName}
                            </Typography>
                            {documentDescription && (
                                <Typography
                                    component="div"
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ lineHeight: 1.5, mt: 0.25, ...descHtmlSx }}
                                    dangerouslySetInnerHTML={{
                                        __html: linkifyHtml(sanitizeHtml(documentDescription)),
                                    }}
                                />
                            )}
                        </Box>

                        {/* Timestamp */}
                        {document.resource_name && (
                            <Typography
                                variant="caption"
                                color="text.disabled"
                                sx={{ fontSize: '0.688rem', flexShrink: 0 }}
                            >
                                {new Date(document.timestamp).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                })}
                            </Typography>
                        )}
                    </Box>
                ) : (
                    /* ═══ GRID / CARD LAYOUT ═══ */
                    <>
                        {/* Status + Date */}
                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{
                                mb: 1,
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}
                        >
                            <Chip
                                icon={getStatusIcon(document.status)}
                                color={getStatusColor(document.status)}
                                label={formatStatusLabel(document.status)}
                                size="small"
                                variant="outlined"
                                sx={{ fontWeight: 600, fontSize: '0.72rem', height: 24 }}
                            />
                            {document.resource_name && (
                                <Typography
                                    variant="caption"
                                    color="text.disabled"
                                    sx={{ fontSize: '0.688rem' }}
                                >
                                    {new Date(document.timestamp).toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric',
                                    })}
                                </Typography>
                            )}
                        </Stack>

                        {/* Document Name + info icon (only when text is clamped) */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600, fontSize: '0.875rem' }}
                            >
                                {documentName}
                            </Typography>
                            {documentDescription && isClamped && !descExpanded && (
                                <ClickAwayListener onClickAway={handleInfoClose}>
                                    <HtmlTooltip
                                        open={openInfo}
                                        onOpen={() => setOpenInfo(true)}
                                        onClose={handleInfoClose}
                                        title={
                                            <Box
                                                sx={{
                                                    maxWidth: 360,
                                                    maxHeight: 240,
                                                    overflowY: 'auto',
                                                }}
                                                dangerouslySetInnerHTML={{
                                                    __html: linkifyHtml(
                                                        sanitizeHtml(documentDescription)
                                                    ),
                                                }}
                                            />
                                        }
                                        placement="top"
                                        slotProps={{ tooltip: { sx: { p: 1 } } }}
                                    >
                                        <IconButton
                                            size="small"
                                            aria-label="explanation"
                                            onClick={handleInfoClick}
                                            onMouseDown={(e) => e.stopPropagation()}
                                            sx={{ p: 0.5 }}
                                        >
                                            <InfoOutlined fontSize="small" />
                                        </IconButton>
                                    </HtmlTooltip>
                                </ClickAwayListener>
                            )}
                        </Box>

                        {/* Description — 2-line clamp, click to expand/collapse */}
                        {documentDescription && (
                            <Typography
                                ref={descRef}
                                component="div"
                                variant="caption"
                                color="text.secondary"
                                onClick={() => setDescExpanded((v) => !v)}
                                sx={{
                                    cursor: isClamped || descExpanded ? 'pointer' : 'default',
                                    lineHeight: 1.5,
                                    fontSize: '0.8rem',
                                    ...(!descExpanded && {
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                    }),
                                    ...(descExpanded && {
                                        maxHeight: 160,
                                        overflowY: 'auto',
                                    }),
                                    ...descHtmlSx,
                                    ...((isClamped || descExpanded) && {
                                        '&:hover': { color: 'text.primary' },
                                    }),
                                }}
                                dangerouslySetInnerHTML={{
                                    __html: linkifyHtml(sanitizeHtml(documentDescription)),
                                }}
                            />
                        )}
                    </>
                )}
            </CardContent>

            {/* ── Action buttons ── */}
            <Box
                sx={{
                    px: { xs: 1.25, sm: 1.5 },
                    pb: { xs: 1.25, sm: 1.5 },
                    pt: isRow ? { xs: 1.25, sm: 1.5 } : 0,
                    ...(isRow && {
                        borderLeft: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        flexShrink: 0,
                    }),
                }}
            >
                {renderActions()}
            </Box>
        </Card>
    );
};

export default DocumentCard;
